import AdditionalInformation from '../AdditionalInformation';
import PropTypes from 'prop-types';
import QuantityNumericInput from '../QuantityNumericInput';
import React from 'react';
import styles from './OptionQuantity.scss';
import { useDebouncedCallback } from 'use-debounce';

const OptionQuantity = React.forwardRef(
  (
    {
      isInvalid = false,
      option,
      parentOption,
      quantity,
      showOptionName = true,
      step = 1,
      onQuantityChanged,
      onSaveQuantity,
    },
    ref
  ) => {
    const { codeValue, description, isOutOfStock, isSoldOut, specialBomCharge } = option;
    const optionKey = `option${!!parentOption ? `-${parentOption.codeValue}` : ''}-${codeValue}`;
    const additionalInfoId = `${optionKey}-additional-info`;
    const inputId = `${optionKey}-quantity-input`;
    const label = `Enter the quantity of ${description}${!!parentOption ? ` of ${parentOption.description}` : ''}:`;
    const hasAdditionalInformation = isOutOfStock || isSoldOut || !!specialBomCharge;

    const handleSaveQuantity = () => onSaveQuantity(option);

    const debouncedSaveQuantity = useDebouncedCallback(() => {
      handleSaveQuantity();
    }, 300);

    const handleChange = (key, qty) => {
      onQuantityChanged(key, qty);
      debouncedSaveQuantity();
    };

    return (
      <div className={`${styles['option-quantity']}${isInvalid ? ` ${styles['invalid']}` : ''}`}>
        {showOptionName && <div className={styles['option-name']}>{description}</div>}
        <QuantityNumericInput
          aria-describedby={hasAdditionalInformation ? additionalInfoId : undefined}
          aria-label={label}
          disabled={isSoldOut}
          id={inputId}
          isInvalid={isInvalid}
          quantityKey={codeValue}
          ref={ref}
          step={step}
          value={quantity}
          onBlur={handleSaveQuantity}
          onChange={handleChange}
          onPressEnter={handleSaveQuantity}
        />
        {hasAdditionalInformation && <AdditionalInformation id={additionalInfoId} option={option} />}
      </div>
    );
  }
);

const OptionShape = PropTypes.shape({
  code: PropTypes.string.isRequired,
  codeValue: PropTypes.string.isRequired,
  colorGroupList: PropTypes.arrayOf(PropTypes.string).isRequired,
  colorReference: PropTypes.string.isRequired,
  componentId: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  estimatedShipDate: PropTypes.string.isRequired,
  futureShipDateSettingStatus: PropTypes.oneOf([0, 1, 2]).isRequired,
  helpLink: PropTypes.shape({
    helpText: PropTypes.string.isRequired,
    linkText: PropTypes.string.isRequired,
  }),
  inventoryMessage: PropTypes.string.isRequired,
  isFutureShipDateSettable: PropTypes.bool.isRequired,
  isOutOfStock: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool.isRequired,
  isSoldOut: PropTypes.bool.isRequired,
  itemId: PropTypes.string.isRequired,
  optionText: PropTypes.string.isRequired,
  outOfStockMessage: PropTypes.string.isRequired,
  quantity: PropTypes.number.isRequired,
  sequence: PropTypes.number.isRequired,
  specialBomCharge: PropTypes.string.isRequired,
  useTexture: PropTypes.bool.isRequired,
  yearOptionPricing: PropTypes.oneOf([0, 1, 2]).isRequired,
});

OptionQuantity.propTypes = {
  isInvalid: PropTypes.bool,
  option: OptionShape.isRequired,
  parentOption: OptionShape,
  quantity: PropTypes.number.isRequired,
  showOptionName: PropTypes.bool,
  step: PropTypes.number,
  onQuantityChanged: PropTypes.func.isRequired,
  onSaveQuantity: PropTypes.func.isRequired,
};

export default OptionQuantity;
